// src/components/ChatConversation.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchMessages, sendMessage } from "../../redux/actions/matchActions";
import { useWS } from "../UseWebSocket";
import "./styles.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage, faMicrophone, faPaperPlane, faPaperclip } from "@fortawesome/free-solid-svg-icons";
import { Document, Page, pdfjs } from "react-pdf";
import PdfModal from "../PdfModal";
import EmojiPicker from "emoji-picker-react";


pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
const ChatConversation = ({ toUserId, onBack, onMessageSent }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [typeMessage, setTypeMessage] = useState("text");
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const [newMessage, setNewMessage] = useState("");
  const { token, user } = useSelector((state) => state.auth || {});
  const { selectedMatch } = useSelector((state) => state.match);
  const typingUsers = useSelector((state) => state.typing.typingUsers);

  const match = selectedMatch;
  const messages = selectedMatch?.messages || [];
  const dispatch = useDispatch();

  const messagesEndRef = useRef(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  const openPdf = (file, categorie) => {
    setPdfUrl(`${apiUrl}/api/files/?categorie=${categorie}&file=${file}`);
  };
  const { sendTypingStatus } = useWS();

  // Scroll automatique jusqu'au dernier message
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Typing avec debounce
  const typingTimeoutRef = useRef(null);
  const handleTyping = () => {
    if (!sendTypingStatus || !match) return;

    sendTypingStatus(match.id, toUserId, true);

    // Reset du timer
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingStatus(match.id, toUserId, false);
    }, 1500); // 1.5s après le dernier caractère
  };

  const handleEmojiClick = (emojiData) => {
    setNewMessage((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };


  const handleSelectImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setTypeMessage("image");
  };

  const handleSelectDocument = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setTypeMessage("document");
  };

  const handleSend = () => {
    if (typeMessage === "text" && !newMessage.trim()) return;
    if (typeMessage !== "text" && !selectedFile) return;

    dispatch(
      sendMessage(token, {
        matchId: match.id,
        toUserId,
        typeMessage,
        messageTxt: newMessage,
        file: selectedFile,
      })
    ).then(() => dispatch(fetchMessages(token, match.id)));

    if (sendTypingStatus) sendTypingStatus(match.id, toUserId, false);

    setNewMessage("");
    setSelectedFile(null);
    setTypeMessage("text");

    if (onMessageSent) onMessageSent();
  };


  const handleChange = (e) => {
    setNewMessage(e.target.value);
    handleTyping();
  };

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    mediaRecorderRef.current = new MediaRecorder(stream);
    audioChunksRef.current = [];

    mediaRecorderRef.current.ondataavailable = (event) => {
      audioChunksRef.current.push(event.data);
    };

    mediaRecorderRef.current.onstop = () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
      const audioFile = new File([audioBlob], "vocal.webm", { type: "audio/webm" });

      dispatch(
        sendMessage(token, {
          matchId: match.id,
          toUserId,
          typeMessage: "vocal",
          messageTxt: null,
          file: audioFile,
        })
      ).then(() => dispatch(fetchMessages(token, match.id)));

      if (sendTypingStatus) sendTypingStatus(match.id, toUserId, false);

      setIsRecording(false);
    };


    mediaRecorderRef.current.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setIsRecording(false);
  };

  const formatMessageTime = (createdAt) => {
    const date = new Date(createdAt + "Z");
    return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  };

  // 👇 Fonction pour grouper les messages par jour
  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach((msg) => {
      const date = new Date(msg.createdAt + "Z");
      const now = new Date();
      const yesterday = new Date();
      yesterday.setDate(now.getDate() - 1);

      let key = "";
      if (
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear()
      ) {
        key = "Aujourd’hui";
      } else if (
        date.getDate() === yesterday.getDate() &&
        date.getMonth() === yesterday.getMonth() &&
        date.getFullYear() === yesterday.getFullYear()
      ) {
        key = "Hier";
      } else {
        key = date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
      }

      if (!groups[key]) groups[key] = [];
      groups[key].push(msg);
    });

    return groups;
  };


  const groupedMessages = groupMessagesByDate(messages);
  const isTyping = typingUsers?.[match?.id]?.[toUserId] || false;

  return (
    <div className="chat-conversation">
      <div
        className="chat-messages"
        style={{ overflowY: "auto", maxHeight: "400px", padding: "10px" }}
      >
        {Object.entries(groupedMessages).map(([dayLabel, msgs]) => (
          <div key={dayLabel}>
            <div
              className="day-label"
              style={{ textAlign: "center", margin: "10px 0", color: "#888", fontWeight: "bold" }}
            >
              {dayLabel}
            </div>
            {msgs.map((m, index) => (
              <div
                key={index}
                className={`chat-message ${m.fromUserId === user.idUser ? "sent" : "received"}`}
                style={{ marginBottom: "8px" }}
              >
                <h3 style={{ margin: "0 0 4px 0", fontSize: "0.85rem" }}>
                  {m.fromUserId === user.idUser
                    ? "Moi"
                    : match.prestataire?.firstNamePrestataire || match.client?.firstNameClient}
                </h3>
                <div style={{ margin: "0" }}>
                  {m.typeMessage === "text" && <p style={{ margin: "0" }}>{m.messageTxt}</p>}

                  {m.typeMessage === "image" && m.imgFileName && (
                    <img
                      src={`${process.env.REACT_APP_API_URL}/api/files?categorie=messages&file=${m.imgFileName}`}
                      alt="Envoyé"
                      style={{ maxWidth: "200px", borderRadius: "6px" }}
                    />
                  )}

                  {m.typeMessage === "document" && m.docFileName && (
                    <div
                      className="pdf-thumbnail"
                      onClick={() => openPdf(m.docFileName, "messages")}
                    >
                      <Document file={`${process.env.REACT_APP_API_URL}/api/files/?categorie=messages&file=${m.docFileName}`}>
                        <Page pageNumber={1} width={200} />
                      </Document>
                      <button
                        className="btn-primary"
                        onClick={(e) => {
                          e.stopPropagation(); // 🔥 empêche l’ouverture de la modal
                          window.open(
                            `${process.env.REACT_APP_API_URL}/api/files/?categorie=messages&file=${m.docFileName}&download=1`,
                            "_blank"
                          );
                        }}
                      >
                        TÉLÉCHARGER
                      </button>

                    </div>
                  )}

                  {m.typeMessage === "vocal" && m.vocalFileName && (
                    <audio controls style={{ width: "100%" }}>
                      <source
                        src={`${process.env.REACT_APP_API_URL}/api/files?categorie=messages&file=${m.vocalFileName}`}
                        type="audio/webm"
                      />
                      Votre navigateur ne supporte pas la lecture audio.
                    </audio>
                  )}
                </div>

                <span className="time" style={{ fontSize: "0.75rem", color: "#999" }}>
                  {formatMessageTime(m.createdAt)}
                </span>
              </div>
            ))}
          </div>
        ))}
        <div ref={messagesEndRef} />

      </div>
      {isTyping && (
        <div style={{ fontStyle: "italic", color: "#555", margin: "5px 0" }}>
          En train de taper...
        </div>
      )}
      <div
        className="chat-input"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "8px",
        }}
      >
        {/* INPUT CONTAINER */}
        <div
          style={{
            flexGrow: 1,
            minWidth: 0, // 🔥 CRUCIAL pour flex
            position: "relative",
          }}
        >
          <input
            type="text"
            placeholder="Message"
            value={newMessage}
            onChange={handleChange}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            style={{
              width: "100%",
              padding: "10px 80px 10px 14px",
              borderRadius: "24px",
              border: "1px solid #ddd",
              fontSize: "14px",
              boxSizing: "border-box", // 🔥 CRUCIAL
            }}
          />

          {/* Icônes DANS l’input */}
          <div
            style={{
              position: "absolute",
              right: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              gap: "14px",
              pointerEvents: "auto",
            }}
          >
            <label
              style={{ cursor: "pointer", color: "#666" }}
              onClick={() => setShowEmojiPicker((v) => !v)}
            >
              😊
            </label>

            <label style={{ cursor: "pointer", color: "#666" }}>
              <FontAwesomeIcon icon={faImage} />
              <input type="file" accept="image/*" hidden onChange={handleSelectImage} />
            </label>

            <label style={{ cursor: "pointer", color: "#666" }}>
              <FontAwesomeIcon icon={faPaperclip} />
              <input type="file" hidden onChange={handleSelectDocument} />
            </label>
          </div>
        </div>

        {/* BOUTON ACTION (MICRO / SEND) */}
        <button
          onClick={
            newMessage.trim() || selectedFile
              ? handleSend
              : isRecording
                ? stopRecording
                : startRecording
          }
          style={{
            width: "44px",
            height: "44px",
            borderRadius: "50%",
            border: "none",
            backgroundColor:
              newMessage.trim() || selectedFile
                ? "#007bff"
                : isRecording
                  ? "#dc3545"
                  : "#28a745",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0, // 🔥 EMPÊCHE D’ÊTRE ÉCRASÉ
          }}
        >
          <FontAwesomeIcon
            icon={
              newMessage.trim() || selectedFile ? faPaperPlane : faMicrophone
            }
          />
        </button>
      </div>
      {showEmojiPicker && (
        <div
          style={{
            position: "absolute",
            bottom: "60px",
            right: "0",
            zIndex: 1000,
          }}
        >
          <EmojiPicker
            onEmojiClick={handleEmojiClick}
            searchDisabled
            skinTonesDisabled
            height={350}
            width={300}
          />
        </div>
      )}






      {/* Affichage du fichier sélectionné */}
      {selectedFile && (
        <div style={{ fontSize: "0.85rem", color: "#555", marginTop: "4px" }}>
          {typeMessage} prêt : {selectedFile.name}
        </div>
      )}

      {pdfUrl && <PdfModal fileUrl={pdfUrl} onClose={() => setPdfUrl(null)} />}

    </div>
  );
};

export default ChatConversation;
